import * as React from 'react';
import { MapPin } from 'lucide-react';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { Select } from '@/components/forms/Select';
import { useSessionFormData } from '@/hooks/useSessionFormData';
import { useAssignmentEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import {
  ESTABLISHMENT_SEARCH_HINT,
  formatEstablishmentSelectLabel,
  stubEstablishmentForSelect,
} from '@/utils/format-establishment-select-label-utils';
import { toIdLabelOptions } from '@/components/events/event-form-utils';
import type { Establishment } from '@/types/establishment.types';
import type {
  EventFormFieldChange,
  EventFormFieldValidation,
  EventFormState,
} from '@/types/event.types';

/** Props for {@link EventGeneralSection}. */
interface Props {
  form: EventFormState;
  onFieldChange: EventFormFieldChange;
  onBlur: (field: string) => void;
  validation: EventFormFieldValidation;
  selectedEstablishment?: Pick<Establishment, 'id' | 'name'> | null;
}

/** Title, type, and general event fields. */
export function EventGeneralSection({
  form,
  onFieldChange,
  onBlur,
  validation,
  selectedEstablishment,
}: Readonly<Props>) {
  const { establishments, classrooms } = useSessionFormData();
  const {
    options: establishmentSearchOptions,
    isLoading: establishmentSearchLoading,
    onSearch: handleEstablishmentSearch,
  } = useAssignmentEstablishmentsAutocomplete();

  const establishmentOptions = React.useMemo(() => {
    const id = form.establishmentId;
    if (!id) return establishmentSearchOptions;
    const existsInSearch = establishmentSearchOptions.some((opt) => opt.value === id);
    if (existsInSearch) return establishmentSearchOptions;
    const fromList = (establishments as Establishment[]).find((e) => e.id === id);
    const resolved = stubEstablishmentForSelect(id, fromList ?? selectedEstablishment);
    if (!resolved) return establishmentSearchOptions;
    return [
      { value: resolved.id, label: formatEstablishmentSelectLabel(resolved) },
      ...establishmentSearchOptions,
    ];
  }, [form.establishmentId, establishments, establishmentSearchOptions, selectedEstablishment]);

  const classroomOptions = toIdLabelOptions(
    classrooms as Array<{ id: string; name: string }>,
    (room) => room.name,
  );

  function handleEstablishmentChange(value: string): void {
    onFieldChange('establishmentId', value);
  }
  function handleEstablishmentBlur(): void {
    onBlur('establishmentId');
  }
  function handleRoomChange(value: string): void {
    onFieldChange('roomId', value);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <MapPin className="w-4 h-4" />
        Informations générales
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Autocomplete
          label="Établissement"
          placeholder="Rechercher un établissement..."
          options={establishmentOptions}
          value={form.establishmentId || ''}
          onChange={handleEstablishmentChange}
          onSearch={handleEstablishmentSearch}
          isLoading={establishmentSearchLoading}
          onBlur={handleEstablishmentBlur}
          state={validation.getFieldState('establishmentId')}
          error={validation.getFieldError('establishmentId')}
          helperText={ESTABLISHMENT_SEARCH_HINT}
          required
          minSearchLength={2}
          inputSize="sm"
        />
        <Select
          label="Salle"
          placeholder="Sélectionner une salle"
          options={classroomOptions}
          value={form.roomId || ''}
          onChange={handleRoomChange}
          size="sm"
        />
      </div>
    </div>
  );
}
